/**
 * useFinanceGames — ленивые списки для модалок EO «Финансы»: «Отведённые игры»
 * (GET /api/finance/games-list) и «Предстоящие игры» (GET /api/finance/
 * future-bookings), оба ?from&to, perm finance:read (finance.ts:209-271).
 * Загрузка — по открытию модалки, с кэшем на период; смена from/to сбрасывает
 * оба списка (V1 index.html:7611). Поля строк — wire snake_case как в роуте.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export interface FinGameRow {
  date: string; time: string; quest: string; branch_name: string;
  total: number; expected_total: number;
  cash: number; terminal: number; transfer: number; prepaid_used: number; invoice: number;
  /** архив 1С (В8, 16.10): «Друг.» отчёта и признак игры 1С (1 — её other = «Проч. 1С»); старый сервер — нет полей */ other?: number; is_1c?: number;
  players: number; actual_players: number | null; report_id: string | number;
}
export interface FinFutureRow {
  id: string | number; date: string; time: string; quest: string; quest_name: string;
  branch_name: string; players: number; client_name: string | null;
  prepaid: number | null; prepaid_amount: number | null; est_revenue: number;
}

export interface FinListState<T> { rows: T[] | null; loading: boolean; error: string | null }

const IDLE = { rows: null, loading: false, error: null };

export interface FinanceGamesResult {
  games: FinListState<FinGameRow>;
  future: FinListState<FinFutureRow>;
  loadGames: () => void;
  loadFuture: () => void;
}

export function useFinanceGames(from: string, to: string): FinanceGamesResult {
  const [games, setGames] = useState<FinListState<FinGameRow>>(IDLE);
  const [future, setFuture] = useState<FinListState<FinFutureRow>>(IDLE);
  const gAbort = useRef<AbortController | null>(null);
  const fAbort = useRef<AbortController | null>(null);

  /* Смена периода → кэш обоих списков недействителен. */
  useEffect(() => {
    gAbort.current?.abort();
    fAbort.current?.abort();
    setGames(IDLE);
    setFuture(IDLE);
    return () => { gAbort.current?.abort(); fAbort.current?.abort(); };
  }, [from, to]);

  const load = useCallback(<T,>(
    path: string,
    state: FinListState<T>,
    set: (s: FinListState<T>) => void,
    abortRef: { current: AbortController | null },
  ) => {
    if (!from || !to || state.rows || state.loading) return; // кэш периода жив / уже грузим
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    set({ rows: null, loading: true, error: null });
    api<{ games?: T[]; error?: string }>(`${path}?from=${from}&to=${to}`, { signal: ac.signal })
      .then((r) => {
        if (ac.signal.aborted) return;
        if (r?.error) set({ rows: null, loading: false, error: r.error });
        else set({ rows: r?.games ?? [], loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        set({ rows: null, loading: false, error: e instanceof Error ? e.message : "Ошибка загрузки" });
      });
  }, [from, to]);

  const loadGames = useCallback(
    () => load("/api/finance/games-list", games, setGames, gAbort),
    [load, games],
  );
  const loadFuture = useCallback(
    () => load("/api/finance/future-bookings", future, setFuture, fAbort),
    [load, future],
  );

  return { games, future, loadGames, loadFuture };
}
